import { useGLTF } from '@react-three/drei'
import { useAppSelector } from 'common/hooks/redux'
import { useFetchProceduresQuery } from 'features/Procedure/state/procedure.api'
import {
  DEFAULT_MODEL_VERSION,
  EGender,
  EModelVersion,
} from 'features/Procedure/state/procedure.type'
import { useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router'
import { useSceneActions } from './useSceneActions'

const MODEL_BASE_PATH = '/models'
export const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/v1/decoders/'

export const getModelPath = (version: EModelVersion): string => `${MODEL_BASE_PATH}/${version}.glb`

const V2_VERSIONS: ReadonlySet<EModelVersion> = new Set([
  EModelVersion.female_v2,
  EModelVersion.male_v2,
])

export const isV2ModelVersion = (version: EModelVersion | null | undefined): boolean =>
  version != null && V2_VERSIONS.has(version)

export const isV2ModelPath = (modelPath: string): boolean =>
  Array.from(V2_VERSIONS).some((v) => modelPath.endsWith(`/${v}.glb`))

useGLTF.preload(getModelPath(DEFAULT_MODEL_VERSION), DRACO_DECODER_PATH)

export const isModelVersionValid = (modelVersion: string) => {
  return Object.values(EModelVersion).includes(modelVersion as EModelVersion)
}

export const getHighestModelVersion = (patientSex: string) => {
  return Object.values(EModelVersion)
    .filter((version) => version.startsWith(patientSex))
    .sort((a, b) => {
      return b.localeCompare(a)
    })[0]
}

export const normalizePatientSexForModel = (selectedPatientSex?: string | null): EGender | null => {
  const patientSex = selectedPatientSex?.trim().toLowerCase()

  if (patientSex === EGender.MALE || patientSex === 'm') {
    return EGender.MALE
  }

  if (patientSex === EGender.FEMALE || patientSex === 'f') {
    return EGender.FEMALE
  }

  return null
}

export const resolveModelVersionFromApiAndPatientSex = (
  apiModelVersion: string | undefined | null,
  selectedPatientSex?: string | null,
): EModelVersion => {
  if (apiModelVersion && isModelVersionValid(apiModelVersion)) {
    return apiModelVersion as EModelVersion
  }

  const normalized = normalizePatientSexForModel(selectedPatientSex)
  const candidate = getHighestModelVersion(normalized || EGender.FEMALE)
  return isModelVersionValid(candidate) ? candidate : DEFAULT_MODEL_VERSION
}

export const useEvaluateModel = (selectedPatientSex?: string | null) => {
  const { siteId } = useParams<{ siteId: string }>()
  const modelVersion = useAppSelector((state) => state.procedureReducer.modelVersion)
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )

  const prevArgsRef = useRef({ siteId: '', encounterUid: '' })

  const {
    data: proceduresData,
    isLoading: isLoadingProcedures,
    isFetching: isFetchingProcedures,
  } = useFetchProceduresQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const currentArgs = { siteId: siteId || '', encounterUid: selectedEncounterUid || '' }
  const argsChanged =
    prevArgsRef.current.siteId !== currentArgs.siteId ||
    prevArgsRef.current.encounterUid !== currentArgs.encounterUid
  prevArgsRef.current = currentArgs

  const isLoading = argsChanged ? isFetchingProcedures : isLoadingProcedures

  const { handleSetModelVersion } = useSceneActions()

  const normalizedPatientSex = useMemo(
    () => normalizePatientSexForModel(selectedPatientSex),
    [selectedPatientSex],
  )

  const modelPath = useMemo(() => {
    return getModelPath(modelVersion || DEFAULT_MODEL_VERSION)
  }, [modelVersion])

  useEffect(() => {
    if (isLoading) return

    if (!selectedEncounterUid) return

    if (isFetchingProcedures || !proceduresData) {
      return
    }

    if (modelVersion) {
      return
    }

    handleSetModelVersion(
      resolveModelVersionFromApiAndPatientSex(proceduresData.model_version, selectedPatientSex),
    )
  }, [
    proceduresData,
    isLoading,
    selectedEncounterUid,
    isFetchingProcedures,
    modelVersion,
    selectedPatientSex,
    handleSetModelVersion,
  ])

  const isEvaluated = useMemo(() => {
    return !isLoading && !!modelVersion
  }, [modelVersion, isLoading])

  return {
    modelPath,
    isLoading,
    modelVersion,
    isEvaluated,
    hasDemographicGender: Boolean(normalizedPatientSex),
  }
}
